import { CommonModule } from "@angular/common";
import { Component, DestroyRef, Signal, ViewChild, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Validators } from "@angular/forms";
import { EzFormComponent } from "@template-ui/components/ez-form/ez-form.component";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { ButtonConfig, EzGridDsConfig, InputSelectOption, PanelHeaderConfig } from "@template-ui/types";
import { EzFormInputType, InputConfig } from "@template-ui/types/ez-form.types";
import { ColDef } from "ag-grid-community";
import { PlatformAccessAdminDisplayError, PlatformScopeTypeCatalogItem } from "./platform-access-admin.models";
import { PlatformAccessAdminService, mapPlatformAccessAdminError } from "./platform-access-admin.service";

interface ScopeValueFormValues {
  scopeTypeCode: string;
  scopeKey: string;
  displayName: string;
  description: string;
  isActive: boolean;
}

@Component({
  selector: "phms-platform-access-scopes",
  imports: [CommonModule, UiLibraryModule],
  templateUrl: "./platform-access-scopes.component.html",
  styleUrl: "./platform-access-admin.component.scss",
})
export class PlatformAccessScopesComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformAccessAdminService = inject(PlatformAccessAdminService);

  @ViewChild("scopeValueForm")
  private scopeValueForm?: EzFormComponent;

  readonly pagePanelSubtitle = "Define selectable scope values that can be used when assigning scoped roles or direct permission grants.";
  readonly scopeTypeCatalog = signal<PlatformScopeTypeCatalogItem[]>([]);
  readonly scopeValuesReloadTrigger = signal(0);
  readonly isSaving = signal(false);
  readonly successMessage = signal<string | null>(null);
  readonly errorState = signal<PlatformAccessAdminDisplayError | null>(null);

  readonly scopeTypeOptions = computed<InputSelectOption[]>(() =>
    this.scopeTypeCatalog()
      .filter((scopeType) => scopeType.requiresScopeKey)
      .map((scopeType) => ({
        value: scopeType.code,
        viewValue: `${scopeType.name} (${scopeType.code})`,
      })),
  );

  readonly scopeTypeGridConfig: EzGridDsConfig = this.buildScopeTypeGridConfig();
  readonly scopeValueGridConfig: EzGridDsConfig = this.buildScopeValueGridConfig();
  readonly scopeTypePanelHeaderConfig: PanelHeaderConfig = this.buildScopeTypePanelHeaderConfig();
  readonly scopeValuePanelHeaderConfig: PanelHeaderConfig = this.buildScopeValuePanelHeaderConfig();
  readonly scopeValueFormConfig: InputConfig[] = this.buildScopeValueFormConfig(this.scopeTypeOptions);
  scopeValueFormValues: ScopeValueFormValues = this.createEmptyScopeValueFormValues();

  readonly formActionButtons = computed<ButtonConfig[]>(() => [
    {
      id: "saveScopeValue",
      buttonText: this.isSaving() ? "Saving..." : "Save Scope Value",
      disabled: this.isSaving(),
    },
    {
      id: "resetScopeValue",
      buttonText: "Reset",
      severity: "secondary",
      disabled: this.isSaving(),
    },
  ]);

  constructor() {
    this.loadScopeTypes();
  }

  onFormActionButtonClick(button: ButtonConfig): void {
    switch (button.id) {
      case "saveScopeValue":
        this.saveScopeValue();
        break;
      case "resetScopeValue":
        this.resetScopeValueForm();
        break;
      default:
        break;
    }
  }

  private loadScopeTypes(): void {
    this.platformAccessAdminService
      .listScopeTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (scopeTypes) => this.scopeTypeCatalog.set(scopeTypes),
        error: (error) => this.errorState.set(mapPlatformAccessAdminError(error)),
      });
  }

  private saveScopeValue(): void {
    this.clearMessages();
    const formGroup = this.scopeValueForm?.theFormGroup;
    if (formGroup && !formGroup.valid) {
      formGroup.markAllAsTouched();
      this.errorState.set({
        title: "Validation failed",
        messages: ["Resolve the scope value form errors before saving."],
        issueCodes: [],
      });
      return;
    }

    const values = this.scopeValueFormValues;
    this.isSaving.set(true);
    this.platformAccessAdminService
      .createScopeValue({
        scopeTypeCode: values.scopeTypeCode.trim(),
        scopeKey: values.scopeKey.trim(),
        displayName: values.displayName.trim(),
        description: normalizeOptionalText(values.description),
        isActive: values.isActive,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.successMessage.set("Scope value saved successfully.");
          this.resetScopeValueForm();
          this.scopeValuesReloadTrigger.update((value) => value + 1);
        },
        error: (error) => {
          this.isSaving.set(false);
          this.errorState.set(mapPlatformAccessAdminError(error));
        },
      });
  }

  private resetScopeValueForm(): void {
    this.scopeValueFormValues = this.createEmptyScopeValueFormValues();
    this.scopeValueForm?.clearErrors();
  }

  private clearMessages(): void {
    this.successMessage.set(null);
    this.errorState.set(null);
  }

  private buildScopeTypeGridConfig(): EzGridDsConfig {
    return {
      datasourceId: "platformScopeTypes",
      bindings: [
        {
          bindingType: "READ",
          endpoint: this.platformAccessAdminService.getScopeTypesODataEndpoint(),
          protocol: "GET_ODATA",
        },
      ],
      colDefs: this.buildScopeTypeColumnDefs(),
    };
  }

  private buildScopeValueGridConfig(): EzGridDsConfig {
    return {
      datasourceId: "platformScopeValues",
      bindings: [
        {
          bindingType: "READ",
          endpoint: this.platformAccessAdminService.getScopeValuesODataEndpoint(),
          protocol: "GET_ODATA",
        },
      ],
      colDefs: this.buildScopeValueColumnDefs(),
    };
  }

  private buildScopeTypeColumnDefs(): ColDef[] {
    return [
      { field: "name", headerName: "Scope Type", flex: 1, minWidth: 180 },
      { field: "code", headerName: "Code", flex: 1, minWidth: 150 },
      {
        field: "requiresScopeKey",
        headerName: "Requires Key",
        width: 135,
        valueFormatter: ({ value }) => value ? "Yes" : "No",
      },
      {
        field: "isActive",
        headerName: "Active",
        width: 110,
        valueFormatter: ({ value }) => value ? "Yes" : "No",
      },
    ];
  }

  private buildScopeValueColumnDefs(): ColDef[] {
    return [
      { field: "scopeTypeName", headerName: "Scope Type", flex: 1, minWidth: 180 },
      { field: "displayName", headerName: "Scope Value", flex: 1, minWidth: 180 },
      { field: "scopeKey", headerName: "Key", flex: 1, minWidth: 170 },
      { field: "description", headerName: "Description", flex: 2, minWidth: 240 },
      {
        field: "isActive",
        headerName: "Active",
        width: 110,
        valueFormatter: ({ value }) => value ? "Yes" : "No",
      },
    ];
  }

  private buildScopeTypePanelHeaderConfig(): PanelHeaderConfig {
    return {
      panelHeaderTitle: "Scope Types",
      titleClickToReload: true,
    };
  }

  private buildScopeValuePanelHeaderConfig(): PanelHeaderConfig {
    return {
      panelHeaderTitle: "Scope Values",
      titleClickToReload: true,
    };
  }

  private buildScopeValueFormConfig(scopeTypeOptions: Signal<InputSelectOption[]>): InputConfig[] {
    return [
      {
        name: "scopeTypeCode",
        type: EzFormInputType.SELECT,
        label: "Scope Type",
        row: 1,
        width: "1x",
        options: scopeTypeOptions,
        validation: [Validators.required],
        validationMessages: {
          required: "Scope type is required.",
        },
      },
      {
        name: "scopeKey",
        type: EzFormInputType.TEXT,
        label: "Scope Key",
        row: 1,
        width: "1x",
        validation: [Validators.required],
        validationMessages: {
          required: "Scope key is required.",
        },
      },
      {
        name: "displayName",
        type: EzFormInputType.TEXT,
        label: "Display Name",
        row: 2,
        width: "1x",
        validation: [Validators.required],
        validationMessages: {
          required: "Display name is required.",
        },
      },
      {
        name: "isActive",
        type: EzFormInputType.CHECKBOX,
        label: "Active",
        row: 2,
        width: "1x",
      },
      {
        name: "description",
        type: EzFormInputType.TEXTAREA,
        label: "Description",
        row: 3,
        width: "fill",
      },
    ];
  }

  private createEmptyScopeValueFormValues(): ScopeValueFormValues {
    return {
      scopeTypeCode: "",
      scopeKey: "",
      displayName: "",
      description: "",
      isActive: true,
    };
  }
}

function normalizeOptionalText(value: string | null | undefined): string | null {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : null;
}
